import type { Snippet } from '../snippetTypes.ts';

/**
 * Module-level registry for one-time callbacks that fire when the current in-flight fetch completes.
 * These callbacks are cleared after each fetch completes and are NOT triggered by future fetches.
 */
let pendingLoadCallbacks: Array<(snippets: Snippet[]) => void> = [];

/**
 * Registers a one-time callback that fires when the current in-flight fetch completes.
 *
 * The caller is responsible for checking the state before calling this function.
 * Only call this when you know a fetch is in progress (isLoading=true && isLoaded=false).
 *
 * This is useful when a consumer takes a snapshot and discovers data is still loading -
 * they can register a callback to be notified when the fetch completes without
 * subscribing to store updates.
 *
 * @param callback - Function to call with the loaded snippets when fetch completes
 */
export const onCurrentFetchComplete = (callback: (snippets: Snippet[]) => void): void => {
	pendingLoadCallbacks.push(callback);
};

/**
 * Invokes all pending load callbacks and clears the registry.
 * Called internally by fetchActions when a fetch completes successfully.
 * @internal
 */
export const invokePendingLoadCallbacks = (snippets: Snippet[]): void => {
	const callbacks = pendingLoadCallbacks;
	pendingLoadCallbacks = []; // Clear before invoking to prevent re-registration issues
	callbacks.forEach((cb) => cb(snippets));
};
